/**
 * 커뮤니티 글 작성 폼 컴포넌트
 * Dialog 프리미티브 위에 올라간 모달 폼 — 제목·내용·비밀번호를 받아 새 글을 익명으로 올린다.
 * (데스크톱: 중앙 다이얼로그 / 모바일: 바텀시트, 포커스 트랩·ESC·스크림·스크롤 잠금은 Dialog가 맡는다)
 */

import React, { useState, useRef } from 'react';
import { communityApi, validatePostForm, getErrorMessage } from '../../api/communityApi';
import Dialog from '../common/Dialog';
import ModalActions from '../common/ModalActions';
import Notice from '../common/Notice';
import { useToast } from '../Toast';
import { useConfirm } from '../common/ConfirmDialog';
import './CreatePostForm.css';

// 글자수 제한
const TITLE_MAX_LENGTH = 100;
const CONTENT_MAX_LENGTH = 2000;
const PASSWORD_MAX_LENGTH = 12;

const CreatePostForm = ({ onPostCreated, onCancel }) => {
  const { showToast } = useToast();
  const openConfirm = useConfirm();

  // 상태 관리
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // 열릴 때 제목 입력으로 포커스 (Dialog initialFocus)
  const titleInputRef = useRef(null);

  /**
   * 실시간 유효성 검사 (제출 시도 후에만)
   */
  const validateRealtime = () => {
    if (submitAttempted) {
      const validation = validatePostForm(title, content, password);
      setErrors(validation.errors);
    }
  };

  /**
   * 제목 변경 핸들러
   */
  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    if (newTitle.length <= TITLE_MAX_LENGTH) {
      setTitle(newTitle);
      validateRealtime();
    }
  };

  /**
   * 내용 변경 핸들러
   */
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    if (newContent.length <= CONTENT_MAX_LENGTH) {
      setContent(newContent);
      validateRealtime();
    }
  };

  /**
   * 비밀번호 변경 핸들러
   */
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    if (newPassword.length <= PASSWORD_MAX_LENGTH) {
      setPassword(newPassword);
      validateRealtime();
    }
  };

  /**
   * 폼 제출 핸들러
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setSubmitAttempted(true);

    // 유효성 검사
    const validation = validatePostForm(title, content, password);
    setErrors(validation.errors);

    if (!validation.isValid) {
      return;
    }

    setLoading(true);

    try {
      await communityApi.createPost({
        title: title.trim(),
        content: content.trim(),
        password: password.trim()
      });

      // 성공 알림
      showToast('글을 올렸어요', 'success');

      // 부모 컴포넌트에 알림
      onPostCreated();
    } catch (error) {
      console.error('글 작성 실패:', error);
      showToast(getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 작성 중인 내용이 있을 때 확인 후 취소 (X·스크림·ESC·취소 버튼 모두 이 경로)
   */
  const handleCancel = async () => {
    if (loading) return;
    const hasContent = title.trim() || content.trim() || password.trim();

    if (hasContent) {
      const confirmed = await openConfirm({
        title: '작성 중인 글을 닫을까요?',
        description: '입력한 내용은 저장되지 않아요',
        confirmLabel: '닫기',
        danger: true,
      });
      if (!confirmed) {
        return;
      }
    }

    onCancel();
  };

  const canSubmit = Boolean(title.trim() && content.trim() && password.trim());

  return (
    <Dialog
      closeLabel="글 작성 창 닫기"
      dismissible={!loading}
      initialFocus={titleInputRef}
      onClose={handleCancel}
      open
      panelClassName="community-create-dialog"
      size="md"
      testId="create-post-dialog"
      title="새 글 작성"
    >
      <form
        aria-busy={loading || undefined}
        className="community-create-dialog__form"
        noValidate
        onSubmit={handleSubmit}
      >
        {/* 익명 안내 */}
        <Notice compact testId="create-post-anonymous-notice" variant="info">
          이 글은 익명으로 올라가요. 학교명과 개인정보는 자동으로 가려져요
        </Notice>

        {/* 제목 입력 */}
        <div className="community-create-dialog__field">
          <label className="community-create-dialog__label" htmlFor="community-create-post-title">
            제목 <span aria-hidden="true" className="community-create-dialog__required">*</span>
          </label>
          <input
            aria-describedby={errors.title ? 'community-create-post-title-error' : undefined}
            aria-invalid={errors.title ? 'true' : undefined}
            aria-required="true"
            className={`community-create-dialog__input${errors.title ? ' community-create-dialog__input--error' : ''}`}
            disabled={loading}
            id="community-create-post-title"
            maxLength={TITLE_MAX_LENGTH}
            onChange={handleTitleChange}
            placeholder="글 제목을 입력해주세요"
            ref={titleInputRef}
            type="text"
            value={title}
          />
          <div className="community-create-dialog__field-footer">
            {errors.title && (
              <span className="community-create-dialog__error" id="community-create-post-title-error" role="alert">
                {errors.title}
              </span>
            )}
            <span className="community-create-dialog__count">
              {title.length}/{TITLE_MAX_LENGTH}
            </span>
          </div>
        </div>

        {/* 내용 입력 */}
        <div className="community-create-dialog__field">
          <label className="community-create-dialog__label" htmlFor="community-create-post-content">
            내용 <span aria-hidden="true" className="community-create-dialog__required">*</span>
          </label>
          <textarea
            aria-describedby={errors.content ? 'community-create-post-content-error' : undefined}
            aria-invalid={errors.content ? 'true' : undefined}
            aria-required="true"
            className={`community-create-dialog__input community-create-dialog__textarea${errors.content ? ' community-create-dialog__input--error' : ''}`}
            disabled={loading}
            id="community-create-post-content"
            maxLength={CONTENT_MAX_LENGTH}
            onChange={handleContentChange}
            placeholder="교육 현장 고민이나 경험을 자유롭게 나눠주세요"
            rows="8"
            value={content}
          />
          <div className="community-create-dialog__field-footer">
            {errors.content && (
              <span className="community-create-dialog__error" id="community-create-post-content-error" role="alert">
                {errors.content}
              </span>
            )}
            <span className="community-create-dialog__count">
              {content.length}/{CONTENT_MAX_LENGTH}
            </span>
          </div>
        </div>

        {/* 비밀번호 입력 */}
        <div className="community-create-dialog__field">
          <label className="community-create-dialog__label" htmlFor="community-create-post-password">
            수정/삭제 비밀번호 <span aria-hidden="true" className="community-create-dialog__required">*</span>
          </label>
          <input
            aria-describedby={[
              'community-create-post-password-hint',
              errors.password ? 'community-create-post-password-error' : null,
            ].filter(Boolean).join(' ')}
            aria-invalid={errors.password ? 'true' : undefined}
            aria-required="true"
            autoComplete="new-password"
            className={`community-create-dialog__input${errors.password ? ' community-create-dialog__input--error' : ''}`}
            disabled={loading}
            id="community-create-post-password"
            maxLength={PASSWORD_MAX_LENGTH}
            onChange={handlePasswordChange}
            placeholder="4-12자의 영문, 숫자, 특수문자 조합"
            type="password"
            value={password}
          />
          <div className="community-create-dialog__field-footer">
            {errors.password && (
              <span className="community-create-dialog__error" id="community-create-post-password-error" role="alert">
                {errors.password}
              </span>
            )}
            <span className="community-create-dialog__count">
              {password.length}/{PASSWORD_MAX_LENGTH}
            </span>
          </div>
          <p className="community-create-dialog__hint" id="community-create-post-password-hint">
            이 비밀번호로 나중에 글을 고치거나 지울 수 있어요
          </p>
        </div>

        {/* 작성 가이드 */}
        <Notice testId="create-post-writing-guide" title="작성 가이드" variant="neutral">
          <ul className="community-create-dialog__guide-list">
            <li>다른 선생님을 배려하는 말로 써주세요</li>
            <li>경험을 나누고 서로 도움이 되는 글을 지향해요</li>
          </ul>
        </Notice>

        {/* 채워진 버튼은 "글 올리기" 하나. submit 타입이라 Enter 키 제출과 같은 경로를 탄다 */}
        <ModalActions
          className="community-create-dialog__actions"
          primary={{
            label: '글 올리기',
            type: 'submit',
            loading,
            disabled: !canSubmit,
          }}
          secondary={{ label: '취소', onClick: handleCancel }}
          testId="create-post-actions"
        />
      </form>
    </Dialog>
  );
};

export default CreatePostForm;
